'use client';

// module imports
import { AlertCircle, Facebook, Globe, Instagram, Linkedin, Pencil, Twitter, X, Youtube } from 'lucide-react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { updateUser } from '@/redux/slices/authSlice';
import type { AppDispatch } from '@/redux/store';
import { IUser } from '@/types';

export default function ProfileSocialPresenceCard({ profile }: { profile: IUser }) {
  const dispatch = useDispatch<AppDispatch>();

  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAlert, setShowAlert] = useState(true);

  // Socials config
  const SOCIALS = [
    { key: 'twitter', label: 'Connect to Twitter', icon: <Twitter size={20} className="text-gray-400" />, placeholder: '@my-X-handle' },
    { key: 'facebook', label: 'Connect to Facebook', icon: <Facebook size={20} className="text-gray-400" />, placeholder: 'facebook.com/username' },
    { key: 'instagram', label: 'Connect to Instagram', icon: <Instagram size={20} className="text-gray-400" />, placeholder: '@my-instagram' },
    { key: 'linkedin', label: 'Connect to Linkedin', icon: <Linkedin size={20} className="text-gray-400" />, placeholder: 'linkedin.com/in/username' },
    { key: 'youtube', label: 'Connect to Youtube', icon: <Youtube size={20} className="text-gray-400" />, placeholder: 'youtube.com/@channel' },
    { key: 'website', label: 'Connect Personal Website', icon: <Globe size={20} className="text-gray-400" />, placeholder: 'https://example.com' },
  ] as const;

  // Edit handlers
  const startEdit = (idx: number) => {
    setEditingIdx(idx);
    setEditValue(profile?.socials?.[SOCIALS[idx].key] || '');
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setEditValue('');
  };

  const saveEdit = async (idx: number) => {
    if (!profile) return;
    setSaving(true);
    try {
      const key = SOCIALS[idx].key;
      const newSocials = { ...profile.socials, [key]: editValue };
      const updateData = { socials: newSocials };
      await dispatch(updateUser(updateData)).unwrap();
      setEditingIdx(null);
    } catch (error) {
      console.error('Failed to update social:', error);
    } finally {
      setSaving(false);
    }
  };

  // Visibility handlers
  const isPublicProfile = !!profile?.isPublicProfile;
  const setIsPublicProfile = async (next: boolean) => {
    if (!profile) return;
    setShowAlert(!next); // Hide alert if public
    try {
      // Only send the isPublicProfile field that was updated
      const updateData = { isPublicProfile: next };
      await dispatch(updateUser(updateData)).unwrap();
    } catch (error) {
      console.error('Failed to update profile visibility:', error);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 shadow max-w-5xl mx-auto">
      <h2 className="text-xl sm:text-2xl font-bold text-[#5167b2] mb-4 sm:mb-6">Your Social Presence</h2>

      {/* --- Socials --- */}
      <div>
        {SOCIALS.map((item, idx) => (
          <div key={item.key} className={`flex items-center py-4 px-2 ${idx !== SOCIALS.length - 1 ? 'border-b border-gray-200' : ''}`}>
            <span className="mr-4">{item.icon}</span>
            <span className="flex-1 text-gray-700 font-medium text-sm">{item.label}</span>
            {editingIdx === idx ? (
              <>
                <input type="text" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="border-0 border-b border-gray-400 bg-transparent px-0 py-1 text-gray-700 font-medium w-2/3 mr-4 focus:outline-none focus:border-gray-600" placeholder={item.placeholder} autoFocus />
                <div className="w-24 flex justify-end">
                  <div className="flex gap-4">
                    <button className="text-blue-600 text-sm font-medium cursor-pointer hover:text-blue-800 transition-colors" disabled={saving} onClick={() => saveEdit(idx)}>
                      Save
                    </button>
                    <button className="text-blue-600 text-sm font-medium cursor-pointer hover:text-blue-800 transition-colors" disabled={saving} onClick={cancelEdit}>
                      Cancel
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <span className="text-gray-400 italic ml-2 w-2/3 truncate">{profile?.socials?.[item.key] || ''}</span>
                <div className="w-24 flex justify-end">
                  <button className="flex items-center gap-1 text-blue-600 text-sm font-medium cursor-pointer hover:text-blue-800 transition-colors" onClick={() => startEdit(idx)}>
                    <Pencil size={14} className="inline-block" />
                    Edit
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* --- Profile Visibility Section --- */}
      <div className="mt-6 sm:mt-8 bg-gray-50 rounded-2xl p-4 sm:p-6 mb-2">
        <h3 className="text-xl sm:text-2xl font-bold text-[#5167b2] mb-3 sm:mb-4">Your Profile Visibility</h3>
        {showAlert && !isPublicProfile && (
          <div className="flex items-start sm:items-center border rounded-lg px-3 sm:px-4 py-2 sm:py-3 mb-3 sm:mb-4 border-red-200 bg-red-50 relative">
            <AlertCircle className="text-red-500 mr-2 mt-0.5 sm:mt-0" size={16} />
            <span className="text-red-600 flex-1 text-xs sm:text-sm">Your profile is hidden and only visible to you. Switch to public to share with prospects and get discovered!</span>
            <button className="ml-2 text-red-600 hover:text-red-900" aria-label="Dismiss" onClick={() => setShowAlert(false)}>
              <X size={16} />
            </button>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <span className="text-gray-500 text-sm sm:text-base">{isPublicProfile ? 'Your profile is publicly accessible' : 'Your profile is private and only visible to you'}</span>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-gray-500 text-sm sm:text-base">{isPublicProfile ? 'Make Private' : 'Make Public'}</span>
            <label className="flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only" checked={isPublicProfile} onChange={() => setIsPublicProfile(!isPublicProfile)} />
              <div className={`w-10 h-6 sm:w-12 sm:h-7 flex items-center bg-gray-200 rounded-full p-1 duration-300 ease-in-out ${isPublicProfile ? 'bg-[#5167b2]' : ''}`}>
                <div className={`bg-white w-4 h-4 sm:w-5 sm:h-5 rounded-full shadow-md transform duration-300 ease-in-out ${isPublicProfile ? 'translate-x-4 sm:translate-x-5' : ''}`} />
              </div>
            </label>
            <span className="font-bold text-gray-700 text-sm sm:text-base">{isPublicProfile ? 'Public' : 'Private'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
